// src/components/EventCard.js
// ─────────────────────────────────────────────────────────────────────────────
// Compact event card rendered in the upcoming events list and search results.
//
// Layout:
//   ┌──────┬──────────────────────────────────┬───────────────┐
//   │ Aug  │  Book Club                        │ REGISTER NOW  │
//   │  10  │  Casual book club meetup          │               │
//   │7 PM  │  👤 0 / 5 Joined                  │               │
//   └──────┴──────────────────────────────────┴───────────────┘
//
// Touch / click:
//   • Card body  → calls onCardClick() (parent opens EventModal)
//   • CTA button → executes action (register / join chat) independently
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useContext } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../context/appContext";
import { API_BASE_URL } from "../apiConfig";
import "./EventCard.scss";

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatEventDate(dateStr) {
  if (!dateStr) return { month: "--", day: "--" };
  const parts = dateStr.split("-");
  const month = parseInt(parts[1], 10) - 1;
  const day   = parseInt(parts[2], 10);
  return { month: MONTHS_SHORT[month] ?? "--", day: String(day) };
}

export default function EventCard({ event, onRefresh, onCardClick }) {
  const user       = useSelector((state) => state.user);
  const navigate   = useNavigate();
  const { setCurrentRoom } = useContext(AppContext);
  const [ctaLoading, setCtaLoading] = useState(false);

  const today        = new Date().toISOString().split("T")[0];
  const past         = event.date < today;
  const isRegistered = event.participants && user &&
    event.participants.some((p) => String(p._id || p) === String(user._id || user.id));
  const full         = event.participants.length >= event.maxParticipants;
  const { month, day } = formatEventDate(event.date);
  const timeDisplay    = event.time?.trim() || null;

  // ── CTA label ─────────────────────────────────────────────────────────────
  let ctaLabel   = past ? "Ended" : full && !isRegistered ? "Full" : "Register Now";
  let ctaEnabled = !past && (!full || isRegistered);
  if (isRegistered) ctaLabel = "Join Chat";

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.stopPropagation();
    if (!ctaEnabled || ctaLoading) return;
    setCtaLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/events/${event._id}/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user._id || user.id }),
      });
      if (response.ok) {
        onRefresh();
      } else {
        const data = await response.json();
        alert(data.message || "Failed to register");
      }
    } catch {
      alert("Network error");
    } finally {
      setCtaLoading(false);
    }
  };

  const handleJoinChat = (e) => {
    e.stopPropagation();
    setCurrentRoom(`room_event_${event._id}`);
    navigate("/chat");
  };

  const handleCtaClick = isRegistered ? handleJoinChat : handleRegister;

  // ── Details preview (cap at 80 chars) ────────────────────────────────────
  const rawDetails = event.details || event.description || "";
  const detailsPreview = rawDetails.length > 80
    ? rawDetails.slice(0, 80) + "…"
    : rawDetails;

  return (
    <div
      className={`event-card${past ? " event-card--past" : ""}`}
      onClick={onCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onCardClick?.()}
      aria-label={`View details for ${event.title}`}
    >
      {/* ── Date block ───────────────────────────────────────────────────── */}
      <div className={`event-card__date-block${past ? " event-card__date-block--past" : ""}`}>
        <span className="event-card__month">{month}</span>
        <span className="event-card__day">{day}</span>
        {timeDisplay && <span className="event-card__time">{timeDisplay}</span>}
      </div>

      {/* ── Content ──────────────────────────────────────────────────────── */}
      <div className="event-card__content">
        <div className="event-card__title-row">
          <span className="event-card__title">{event.title}</span>
          {past && <span className="event-card__past-badge">Past</span>}
        </div>
        {detailsPreview && (
          <p className="event-card__details">{detailsPreview}</p>
        )}
        <div className="event-card__stats">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="#9CA3AF" aria-hidden="true">
            <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002a.274.274 0 0 1-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM6.936 9.28a5.88 5.88 0 0 0-1.23-.247A7.35 7.35 0 0 0 5 9c-4 0-5 3-5 4 0 .667.333 1 1 1h4.216A2.238 2.238 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816zM4.92 10A5.493 5.493 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275zM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0zm3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
          </svg>
          <span className="event-card__stats-text">
            {event.participants.length} / {event.maxParticipants} Joined
          </span>
        </div>
      </div>

      {/* ── CTA button ───────────────────────────────────────────────────── */}
      <button
        className={`event-card__cta event-card__cta--${
          past ? "ended" : isRegistered ? "chat" : full ? "full" : "register"
        }`}
        onClick={handleCtaClick}
        disabled={!ctaEnabled || ctaLoading}
        aria-label={ctaLabel}
      >
        {ctaLoading ? <span className="event-card__spinner" /> : ctaLabel}
      </button>
    </div>
  );
}
